import { traducirProblema, useTraduccion, type ProblemaApi } from '@tallerya/i18n';
import {
  AvisoError,
  Boton,
  CampoAreaTexto,
  IconoChevronAbajo,
  Menu,
  useAvisos,
  useFormulario,
  Ventana,
  type ElementoMenu,
} from '@tallerya/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { usePermisos } from '../../permisos/usePermiso';
import {
  useCambiosOrden,
  useClaveDeReintento,
  useEstados,
  version,
  type CabeceraOrden,
  type EstadoOrden,
} from './datos';

// Las acciones de estado de la ficha (#135), como en `190:9042`:
//
//   [Cambiar estado ▾]  [Marcar como Lista]  [•••]
//
// - «Cambiar estado» ofrece todos los estados activos menos el actual.
// - El botón principal es **el siguiente paso** del trabajo (`siguienteEstado`):
//   un toque para lo que se hace todos los días.
// - «•••» tiene Eliminar, para la orden que no debió existir.
//
// Lo que la orden rechaza —para entregarla falta el cliente y el folio— llega
// todo junto y se muestra en **un solo aviso** sobre la ficha (`onProblema`).
// Anular pide el motivo en una ventana.
//
// Entrar a un estado final —Lista, Pagada, Entregada, Anulada— o salir de él
// pide `order.close`, y eliminar `order.delete`: sin el permiso, la opción no se
// ofrece. Ocultar no es proteger: la API lo vuelve a comprobar.

/** Si entrar o salir de ese estado pide `order.close`: los finales, como en la API. */
const esFinal = (e: { isFinal: boolean; category: EstadoOrden['category'] }) =>
  e.isFinal || e.category === 'closed' || e.category === 'cancelled';

/**
 * El estado del botón principal: el siguiente paso del trabajo después del
 * actual, o `null` si no hay ninguno que ofrecer. `estados` vienen activos y en el
 * orden del taller.
 */
export function siguienteEstado(estados: readonly EstadoOrden[], actualId: string): EstadoOrden | null {
  const indice = estados.findIndex((e) => String(e.id) === actualId);
  const actual = estados[indice];

  // Sin siguiente paso: el actual no está en la lista —el taller lo desactivó— o
  // el trabajo ya terminó. «Lista» y «Pagada» son finales, pero falta entregar:
  // por eso se pregunta la categoría y no `isFinal`.
  if (!actual || actual.category === 'closed' || actual.category === 'cancelled') {
    return null;
  }

  // Anular no es avanzar: nunca es el botón principal.
  const siguiente = estados[indice + 1];
  return siguiente && siguiente.category !== 'cancelled' ? siguiente : null;
}

export function AccionesDeEstado({
  orden,
  onProblema,
}: {
  orden: CabeceraOrden;
  /** El texto del aviso, o `null` para quitarlo. */
  onProblema: (texto: string | null) => void;
}) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const navegar = useNavigate();
  const { puede } = usePermisos();
  const cambios = useCambiosOrden();
  const claveDe = useClaveDeReintento();
  const estados = useEstados().data ?? [];

  const [anulando, setAnulando] = useState<EstadoOrden | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [enCurso, setEnCurso] = useState(false);

  const id = String(orden.id);
  const versionLeida = version(orden.version);
  const cierra = puede('order.close');
  const actualEsFinal = esFinal({ isFinal: orden.statusIsFinal, category: orden.statusCategory });

  /** A dónde se puede mover con los permisos de quien mira. */
  const alcanzable = (e: EstadoOrden) =>
    String(e.id) !== String(orden.statusId) && (cierra || (!actualEsFinal && !esFinal(e)));

  /** El texto del aviso para lo que respondió la API, o para una respuesta que no llegó. */
  const textoDe = (problema: ProblemaApi) =>
    problema.code ? traducirProblema(problema, t) : t('ui.formulario.aviso.sinRespuesta');

  /** Manda el cambio. Devuelve lo que la orden rechazó, o nada si salió bien. */
  function enviar(destino: EstadoOrden, motivo?: string) {
    const cuerpo = { id, versionLeida, estado: String(destino.id), motivo };
    return cambios.cambiarEstado(claveDe(cuerpo), id, versionLeida, String(destino.id), motivo);
  }

  function cambiado(destino: EstadoOrden) {
    onProblema(null);
    avisar({ titulo: t('admin.ordenes.estado.cambiado', { estado: destino.name }) });
  }

  async function mover(destino: EstadoOrden) {
    // Anular pide el motivo, en su ventana, antes de ir al servidor.
    if (destino.category === 'cancelled') {
      setAnulando(destino);
      return;
    }

    setEnCurso(true);
    try {
      const problema = await enviar(destino);
      if (problema) onProblema(textoDe(problema));
      else cambiado(destino);
    } catch {
      onProblema(t('ui.formulario.aviso.sinRespuesta'));
    } finally {
      setEnCurso(false);
    }
  }

  const siguiente = siguienteEstado(estados, String(orden.statusId));
  const opciones: ElementoMenu[] = estados.filter(alcanzable).map((e) => ({
    etiqueta: e.name,
    onSeleccionar: () => void mover(e),
    ...(e.category === 'cancelled' && { peligro: true }),
  }));
  const masAcciones: ElementoMenu[] =
    puede('order.delete') && !orden.statusIsFinal
      ? [{ etiqueta: t('admin.ordenes.eliminar.boton'), peligro: true, onSeleccionar: () => setEliminando(true) }]
      : [];

  return (
    <>
      {opciones.length > 0 && (
        <Menu
          elementos={opciones}
          disparador={({ alternar, atributos }) => (
            <Boton variante="secundario" disabled={enCurso} onClick={alternar} {...atributos}>
              {t('admin.ordenes.estado.cambiar')} <IconoChevronAbajo />
            </Boton>
          )}
        />
      )}
      {siguiente && alcanzable(siguiente) && (
        <Boton disabled={enCurso} aria-busy={enCurso} onClick={() => void mover(siguiente)}>
          {t('admin.ordenes.estado.marcarComo', { estado: siguiente.name })}
        </Boton>
      )}
      {masAcciones.length > 0 && (
        <Menu
          elementos={masAcciones}
          disparador={({ alternar, atributos }) => (
            <Boton variante="secundario" soloIcono aria-label={t('admin.ordenes.mas')} onClick={alternar} {...atributos}>
              •••
            </Boton>
          )}
        />
      )}

      {/* Lo que rechace la API se dice en la ventana, con el motivo todavía
          escrito: cerrarla perdería lo que se tecleó. */}
      <VentanaAnular
        destino={anulando}
        onCerrar={() => setAnulando(null)}
        onAnular={(motivo) => (anulando ? enviar(anulando, motivo) : Promise.resolve(undefined))}
        onAnulada={() => {
          if (anulando) cambiado(anulando);
          setAnulando(null);
        }}
      />
      <VentanaEliminar
        abierta={eliminando}
        folio={orden.orderNumber ?? t('admin.ordenes.ficha.sinFolio')}
        onCerrar={() => setEliminando(false)}
        onEliminar={async () => {
          try {
            const problema = await cambios.eliminar(id);
            setEliminando(false);
            if (problema) {
              onProblema(textoDe(problema));
              return;
            }
            avisar({ titulo: t('admin.ordenes.aviso.eliminada') });
            void navegar('/ordenes');
          } catch {
            setEliminando(false);
            onProblema(t('ui.formulario.aviso.sinRespuesta'));
          }
        }}
      />
    </>
  );
}

/** El motivo de anular. Obligatorio: una anulada sin motivo no se anula. */
interface PropsAnular {
  onCerrar: () => void;
  /** Manda el cambio con el motivo: lo que la orden rechace, o nada si salió bien. */
  onAnular: (motivo: string) => Promise<ProblemaApi | undefined>;
  onAnulada: () => void;
}

function VentanaAnular({ destino, ...props }: PropsAnular & { destino: EstadoOrden | null }) {
  if (!destino) return null;
  return <ContenidoAnular key={String(destino.id)} destino={destino} {...props} />;
}

function ContenidoAnular({ destino, onCerrar, onAnular, onAnulada }: PropsAnular & { destino: EstadoOrden }) {
  const { t } = useTraduccion();
  // `guardar` devuelve lo que la orden rechazó, y el formulario lo muestra en su
  // aviso con todo junto; sin red, también lo dice. La ventana solo se cierra si
  // salió bien.
  const form = useFormulario<{ motivo: string }>({
    inicial: { motivo: '' },
    guardar: (v) => onAnular(v.motivo),
  });

  async function anular() {
    if (await form.enviar()) onAnulada();
  }

  return (
    <Ventana
      abierta
      titulo={t('admin.ordenes.anular.titulo', { estado: destino.name })}
      descripcion={t('admin.ordenes.anular.texto')}
      acciones={
        <>
          <Boton variante="texto" disabled={form.guardando} onClick={onCerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton variante="peligro" disabled={form.guardando} onClick={() => void anular()}>
            {t('admin.ordenes.anular.boton')}
          </Boton>
        </>
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoAreaTexto form={form} nombre="motivo" etiqueta={t('admin.ordenes.anular.motivo')} obligatorio ancho="completo" />
    </Ventana>
  );
}

/** Eliminar es corregir un error: lo dice, y recuerda que lo normal es anular. */
function VentanaEliminar({
  abierta,
  folio,
  onCerrar,
  onEliminar,
}: {
  abierta: boolean;
  folio: string;
  onCerrar: () => void;
  onEliminar: () => Promise<void>;
}) {
  const { t } = useTraduccion();
  const [enCurso, setEnCurso] = useState(false);

  return (
    <Ventana
      abierta={abierta}
      titulo={t('admin.ordenes.eliminar.titulo', { folio })}
      descripcion={t('admin.ordenes.eliminar.texto')}
      acciones={
        <>
          <Boton variante="texto" disabled={enCurso} onClick={onCerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton
            variante="peligro"
            disabled={enCurso}
            aria-busy={enCurso}
            onClick={() => {
              setEnCurso(true);
              void onEliminar().finally(() => setEnCurso(false));
            }}
          >
            {t('admin.ordenes.eliminar.boton')}
          </Boton>
        </>
      }
    />
  );
}
